import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { registerSW } from 'virtual:pwa-register';
import { loadAppConfig } from './config/appConfig';

// Extend Window interface for updateSW function
declare global {
  interface Window {
    __updateSW?: ((reloadPage?: boolean) => Promise<void>) | null;
  }
}

import { ThemeProvider } from './theme';
import { DialogProvider } from './components/feedback';
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import { router } from './router';
import './index.css';
import './i18n';

// Import Roboto font
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

// Update title with version info
if (typeof __APP_VERSION__ !== 'undefined') {
  document.title = `Agam ISS v${__APP_VERSION__}`;
}

// Load app config and start the React app
loadAppConfig()
  .then(() => {
    createRoot(document.getElementById('root')!).render(
      <Suspense fallback={<div>Loading...</div>}>
        <ThemeProvider>
          <DialogProvider>
            <GoogleMapsProvider>
              <RouterProvider router={router} />
            </GoogleMapsProvider>
          </DialogProvider>
        </ThemeProvider>
      </Suspense>
    );
  })
  .catch(error => {
    console.error('Failed to initialize app:', error);
  });

// Register service worker asynchronously after app loads
//test
if ('serviceWorker' in navigator) {
  // Declare updateSW globally
  let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;

  // Use requestIdleCallback or setTimeout to defer SW registration
  const registerServiceWorker = () => {
    updateSW = registerSW({
      onNeedRefresh() {
        // Store updateSW function globally and trigger custom event
        window.__updateSW = updateSW;
        window.dispatchEvent(new CustomEvent('sw-need-refresh'));
      },
      onOfflineReady() {
        // App ready to work offline
      },
      onRegistered(registration) {
        // Service Worker registered successfully
        void registration;
      },
      onRegisterError(error: Error) {
        // Service Worker registration failed
        void error;
      },
    });
  };

  // Register SW when browser is idle or after a short delay
  if ('requestIdleCallback' in window) {
    requestIdleCallback(registerServiceWorker);
  } else {
    setTimeout(registerServiceWorker, 1000);
  }
}
